import { useId } from 'react';
import type { CSSProperties } from 'react';

import type { AspectoBotella } from '#components/mobile/budget/recuerdosAspecto';

import {
  ANCHO_BOTELLA,
  burbujas,
  corteLacre,
  cotas,
  LABIO,
  lacre,
  marcasCuello,
  porosCorcho,
  rolloInterior,
  silueta,
} from './geometria';

type BotellaProps = {
  aspecto: AspectoBotella;
  /** Abierta: sin corcho, sin lacre y sin el rollo dentro. */
  abierta?: boolean;
  /**
   * Piezas de la escena de llegada (brillo y gotas). Cada pieza lleva
   * `data-parte` para que la coreografía la encuentre.
   */
  escena?: boolean;
  /** Ancho en px (alto proporcional). Por defecto, 120. */
  ancho?: number;
  className?: string;
  style?: CSSProperties;
};

const capa: CSSProperties = {
  position: 'absolute',
  inset: 0,
  overflow: 'visible',
};

/**
 * La botella de una travesía: vidrio por capas (cuerpo con degradado, rollo
 * de la carta, burbujas, pátina, bandas de luz y grabado del cuello), corcho
 * y lacre. Todo sale del aspecto (`aspectoBotella`), sin azar.
 */
export function Botella({
  aspecto: a,
  abierta = false,
  escena = false,
  ancho = ANCHO_BOTELLA,
  className,
  style,
}: BotellaProps) {
  const id = `b${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const c = cotas(a);
  const s = silueta(a);
  const [dk, md, lg, ed] = a.vidrio.colores;
  const rollo = rolloInterior(a);
  const k = ancho / ANCHO_BOTELLA;
  const caja = `0 0 ${ANCHO_BOTELLA} ${c.lienzo}`;

  return (
    <div
      className={className}
      data-testid="botella"
      data-vidrio={a.vidrio.id}
      data-abierta={abierta || undefined}
      aria-hidden
      style={{
        position: 'relative',
        width: ancho,
        height: c.lienzo * k,
        ...style,
      }}
    >
      <div
        style={{
          ...capa,
          width: ANCHO_BOTELLA,
          height: c.lienzo,
          transform: k === 1 ? undefined : `scale(${k})`,
          transformOrigin: '0 0',
        }}
      >
        <svg
          width={ANCHO_BOTELLA}
          height={c.lienzo}
          viewBox={caja}
          style={capa}
        >
          <defs>
            <linearGradient id={`${id}c`} x1="0" x2="1">
              <stop offset="0" stopColor={ed} />
              <stop offset=".16" stopColor={dk} />
              <stop offset=".42" stopColor={md} stopOpacity=".78" />
              <stop offset=".62" stopColor={md} stopOpacity=".7" />
              <stop offset=".86" stopColor={dk} />
              <stop offset="1" stopColor={ed} />
            </linearGradient>
            <linearGradient id={`${id}b`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fff" stopOpacity=".62" />
              <stop offset=".55" stopColor="#fff" stopOpacity=".22" />
              <stop offset="1" stopColor="#fff" stopOpacity=".04" />
            </linearGradient>
            <radialGradient id={`${id}g`} cx=".32" cy=".38" r=".7">
              <stop offset="0" stopColor={lg} stopOpacity=".55" />
              <stop offset="1" stopColor={lg} stopOpacity="0" />
            </radialGradient>
            <linearGradient id={`${id}r`} x1="0" x2="1">
              <stop offset="0" stopColor="#cdbb98" />
              <stop offset=".45" stopColor="#efe6d3" />
              <stop offset="1" stopColor="#b8a582" />
            </linearGradient>
            <clipPath id={`${id}k`}>
              <path d={s} />
            </clipPath>
          </defs>
          <path d={s} fill={`url(#${id}c)`} />
          <g clipPath={`url(#${id}k)`}>
            {!abierta && (
              <g
                data-parte="rollo-dentro"
                transform={`rotate(-4 60 ${rollo.y + rollo.alto / 2})`}
              >
                <rect
                  x={rollo.x}
                  y={rollo.y}
                  width={rollo.ancho}
                  height={rollo.alto}
                  rx={rollo.ancho / 2}
                  fill={`url(#${id}r)`}
                  opacity=".8"
                />
                <path
                  d={`M${rollo.x - 1} ${rollo.y + rollo.alto * 0.42}h${rollo.ancho + 2}`}
                  stroke="#9c7a3c"
                  strokeWidth="1.4"
                />
              </g>
            )}
            <path d={s} fill={dk} opacity=".32" />
            <path d={s} fill={`url(#${id}g)`} />
            <g fill="none" stroke="#fff" strokeOpacity=".38" strokeWidth=".6">
              {burbujas(a).map((b, i) => (
                <circle key={i} cx={b.cx} cy={b.cy} r={b.r} />
              ))}
            </g>
            <path
              d={s}
              fill="#e9eef0"
              opacity={+(a.patina * 0.3).toFixed(2)}
              data-parte="patina"
            />
            {a.patina > 0.4 && (
              <path
                d={`M${c.izq} ${c.baseHombro + 20} Q60 ${c.baseHombro + 8} ${c.der} ${c.baseHombro + 30}`}
                stroke="#d9c6e8"
                strokeOpacity={+(a.patina * 0.25).toFixed(2)}
                strokeWidth="6"
                fill="none"
              />
            )}
            <rect
              x={c.izq + 7}
              y={c.baseHombro - 2}
              width="7"
              height={c.alto - c.baseHombro - 12}
              rx="3.5"
              fill={`url(#${id}b)`}
            />
            <rect
              x={c.izq + 17}
              y={c.baseHombro + 4}
              width="2.2"
              height={Math.max(0, c.alto - c.baseHombro - 26)}
              rx="1.1"
              fill="#fff"
              opacity=".22"
            />
            <rect
              x={c.cuelloIzq + 3}
              y={LABIO + 11}
              width="3"
              height={a.medidas.altoCuello - 4}
              rx="1.5"
              fill="#fff"
              opacity=".35"
            />
            <path
              d={`M${c.der - 3} ${c.baseHombro + 6}V${c.alto - 14}`}
              stroke="#fff"
              strokeOpacity=".2"
              strokeWidth="1.4"
            />
            <ellipse
              cx="60"
              cy={c.alto - 5}
              rx={a.medidas.anchoCuerpo * 0.34}
              ry="4"
              fill={ed}
              opacity=".7"
            />
            <path
              d={`M${60 - a.medidas.anchoCuerpo * 0.3} ${c.alto - 6} Q60 ${c.alto - 11} ${60 + a.medidas.anchoCuerpo * 0.3} ${c.alto - 6}`}
              stroke="#fff"
              strokeOpacity=".3"
              fill="none"
            />
          </g>
          <ellipse
            cx={c.izq + 14}
            cy={c.baseHombro + 1}
            rx="2.4"
            ry="4.2"
            fill="#fff"
            opacity=".85"
            transform={`rotate(28 ${c.izq + 14} ${c.baseHombro + 1})`}
          />
          <path
            d={marcasCuello(a)}
            stroke="#fff"
            strokeOpacity=".5"
            strokeWidth=".8"
            data-parte="marcas"
            data-marcas={a.marcas}
          />
          <path
            d={s}
            fill="none"
            stroke={ed}
            strokeWidth="1.3"
            strokeOpacity=".85"
          />
          <path
            d={s}
            fill="none"
            stroke="#fff"
            strokeWidth=".5"
            strokeOpacity=".18"
            transform="translate(.8 .4)"
          />
        </svg>

        {escena && (
          <>
            <div
              style={{
                ...capa,
                overflow: 'hidden',
                clipPath: `path('${s}')`,
              }}
            >
              <div
                data-parte="brillo"
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: -70,
                  width: 44,
                  background:
                    'linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.55) 50%,rgba(255,255,255,0))',
                  transform: 'translateX(0) skewX(-14deg)',
                  opacity: 0,
                }}
              />
            </div>
            {[
              60 - a.medidas.anchoCuerpo * 0.28,
              60 + a.medidas.anchoCuerpo * 0.12,
              60 + a.medidas.anchoCuerpo * 0.3,
            ].map((x, i) => (
              <div
                key={i}
                data-parte={`gota${i}`}
                style={{
                  position: 'absolute',
                  left: x,
                  top: c.baseHombro + 8 + i * 9,
                  width: 3,
                  height: 6,
                  borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%',
                  background: 'rgba(255,255,255,.7)',
                  opacity: 0,
                }}
              />
            ))}
          </>
        )}

        {!abierta && (
          <>
            <Corcho aspecto={a} id={id} />
            <MitadLacre aspecto={a} id={id} lado="izq" />
            <MitadLacre aspecto={a} id={id} lado="der" />
          </>
        )}
      </div>
    </div>
  );
}

function Corcho({ aspecto: a, id }: { aspecto: AspectoBotella; id: string }) {
  const c = cotas(a);
  const w = a.medidas.anchoCuello - 3;
  return (
    <svg
      width={ANCHO_BOTELLA}
      height={c.lienzo}
      style={capa}
      data-parte="corcho"
    >
      <defs>
        <linearGradient id={`${id}kc`} x1="0" x2="1">
          <stop offset="0" stopColor="#7d5532" />
          <stop offset=".4" stopColor="#c39463" />
          <stop offset="1" stopColor="#6e4a2b" />
        </linearGradient>
      </defs>
      <rect
        x={c.cuelloIzq + 1.5}
        y={LABIO - 9}
        width={w}
        height="22"
        rx="2"
        fill={`url(#${id}kc)`}
      />
      <g fill="#5a3b20" opacity=".6">
        {porosCorcho(a).map((p, i) => (
          <circle key={i} cx={p.cx} cy={p.cy} r={p.r} />
        ))}
      </g>
    </svg>
  );
}

function MitadLacre({
  aspecto: a,
  id,
  lado,
}: {
  aspecto: AspectoBotella;
  id: string;
  lado: 'izq' | 'der';
}) {
  const c = cotas(a);
  const [tinta, brillo] = a.lacre;
  const pid = `${id}l${lado}`;
  return (
    <svg
      width={ANCHO_BOTELLA}
      height={c.lienzo}
      style={capa}
      data-parte={lado === 'izq' ? 'lacre-izq' : 'lacre-der'}
    >
      <defs>
        <clipPath id={pid}>
          <path d={corteLacre(lado)} />
        </clipPath>
        <radialGradient id={`${pid}g`} cx=".4" cy=".25" r=".8">
          <stop offset="0" stopColor={brillo} />
          <stop offset="1" stopColor={tinta} />
        </radialGradient>
      </defs>
      <g clipPath={`url(#${pid})`}>
        <path
          d={lacre(a)}
          fill={`url(#${pid}g)`}
          stroke="rgba(0,0,0,.25)"
          strokeWidth=".6"
        />
        <circle
          cx="60"
          cy={LABIO - 4}
          r="5"
          fill="none"
          stroke="rgba(0,0,0,.28)"
          strokeWidth="1.1"
        />
        <path
          d={`M56 ${LABIO - 10}q4-2 9 0`}
          stroke="rgba(255,255,255,.35)"
          fill="none"
          strokeWidth="1.2"
        />
      </g>
    </svg>
  );
}
